import InitialState from './InitialState';
import IReducerReturn from '../../../../store/IReducerReturn';
import * as ActionTypes from './ActionTypes';
import * as OrderActionTypes from '../ActionTypes';
import { assign } from '../../../../shared/Assign';
import { IBasicOrderInfo, IOrderRow } from './Types';
import { KeyPickup } from '../../../../shared/ProductProperties';

interface IOrderRowChangeResult {
    orderProductProperties?: Array<{ value: number }>;
}

function syncKeyPickupAddress(
    state: IBasicOrderInfo,
    result: IOrderRowChangeResult,
    submitted?: IOrderRow
): void {
    if (!state.bookingInfo) {
        return;
    }

    // The server's product properties decide first. The edit form is loaded with the stored
    // address for every row, so a submitted address does not mean the order has key pickup.
    const orderProductProperties = result && result.orderProductProperties;
    const serverSentProperties = Array.isArray(orderProductProperties);
    const orderHasKeyPickup =
        serverSentProperties &&
        orderProductProperties.some((property) => property && property.value === KeyPickup);

    if (serverSentProperties && !orderHasKeyPickup) {
        state.bookingInfo = {
            ...state.bookingInfo,
            keyPickupAddress: null,
            keyPickupZip: null,
            keyPickupCity: null
        };
        return;
    }

    if (submitted && submitted.keyPickupAddress) {
        state.bookingInfo = {
            ...state.bookingInfo,
            keyPickupAddress: submitted.keyPickupAddress,
            keyPickupZip: submitted.keyPickupZip,
            keyPickupCity: submitted.keyPickupCity
        };
    }
}

export default function basicOrderInfoReducer(
    state: any = InitialState.basicOrderInfo,
    action: IReducerReturn<IOrderRow>
) {
    let newState: any;

    switch (action.type) {
        case ActionTypes.VIEW_BASIC_INFO_SUCCESS:
            newState = assign({}, action.result);
            return newState;

        case ActionTypes.EDIT_ORDER_ROW_SUCCESS:
            newState = assign({}, state);
            newState.orderRows = [
                ...state.orderRows.filter((x: any) => x.id !== action.result.currentOrderRow.id),
                assign({}, action.result.currentOrderRow)
            ];
            newState.possibleProductsForUpdate = action.result.possibleProductsForUpdate;
            return newState;

        case ActionTypes.UPDATE_ORDER_ROW_SUCCESS:
            newState = assign({}, state);
            newState.orderRows = [
                ...state.orderRows.filter((x: any) => x.id !== action.result.orderRow.id),
                assign({}, action.result.orderRow)
            ];

            if (newState.planInfo && action.result.sketchUpStatus) {
                newState.planInfo.sketchUpStatus = action.result.sketchUpStatus;
            }

            if (newState.planInfo && action.result.roomSketcherStatus) {
                newState.planInfo.roomSketcherStatus = action.result.roomSketcherStatus;
            }

            if (newState.bookingInfo && action.result.imageEditorFirmId) {
                newState.bookingInfo.imageEditorFirmId = action.result.imageEditorFirmId;
            }

            syncKeyPickupAddress(newState, action.result, action.submitted);

            return newState;

        case ActionTypes.CREATE_ORDER_ROW_SUCCESS:
            newState = assign({}, state);
            newState.orderRows = [...state.orderRows, assign({}, action.result.orderRow)];

            if (newState.bookingInfo) {
                newState.bookingInfo.isDirectDelivery = action.result.isDirectDelivery;
            }

            if (newState.planInfo && action.result.sketchUpStatus) {
                newState.planInfo.sketchUpStatus = action.result.sketchUpStatus;
            }

            if (newState.planInfo && action.result.roomSketcherStatus) {
                newState.planInfo.roomSketcherStatus = action.result.roomSketcherStatus;
            }

            if (newState.bookingInfo && action.result.imageEditorFirmId) {
                newState.bookingInfo.imageEditorFirmId = action.result.imageEditorFirmId;
            }

            syncKeyPickupAddress(newState, action.result, action.submitted);

            return newState;

        case ActionTypes.NEW_EMAIL_SUCCESS:
            newState = assign({}, state);
            newState.newEmail = assign({}, action.result);
            return newState;

        case ActionTypes.VIEW_SMS_SUCCESS:
            newState = assign({}, state);
            newState.sms = action.result;
            return newState;

        case ActionTypes.NEW_SMS_SUCCESS:
            newState = assign({}, state);
            newState.newSms = assign({}, action.result);
            return newState;

        case ActionTypes.UPDATE_PREVENT_EMAILS_TO_BROKER_SUCCESS:
            newState = assign({}, state);
            newState.preventSystemEmailsToBroker = action.result;
            return newState;

        case ActionTypes.UPDATE_PREVENT_EMAILS_TO_PHOTOGRAPHER_SUCCESS:
            newState = assign({}, state);
            newState.preventSystemEmailsToPhotographer = action.result;
            return newState;

        case ActionTypes.UPDATE_PREVENT_COMMUNICATION_TO_PROPERTY_OWNER_SUCCESS:
            newState = assign({}, state);
            newState.preventCommunicationToPropertyOwner = action.result;
            return newState;

        case ActionTypes.UNARCHIVE_PHOTO_ORDER_SUCCESS:
            newState = assign({}, state);
            newState.bookingInfo.unarchiveStatus = action.result.unarchiveStatus;
            newState.bookingInfo.rearchiveDate = action.result.rearchiveDate;
            return newState;

        case ActionTypes.UNARCHIVE_PLAN_ORDER_SUCCESS:
            newState = assign({}, state);
            newState.planInfo.unarchiveStatus = action.result.unarchiveStatus;
            newState.planInfo.rearchiveDate = action.result.rearchiveDate;
            return newState;

        case OrderActionTypes.RESET_ORDER_STATES:
            return InitialState.basicOrderInfo;

        case ActionTypes.UPDATE_OWNER_COMMUNICATION_LANGUAGE_SUCCESS:
            newState = assign({}, state);
            newState.ownerCommunication.selectedLanguage = action.result;
            return newState;
        case ActionTypes.UPDATE_IS_DIRECT_DELIVERY_SUCCESS:
            newState = assign({}, state);
            newState.bookingInfo.isDirectDelivery = action.result;
            return newState;

        case ActionTypes.UPDATE_IMAGE_EDITOR_FIRM_SUCCESS:
            newState = assign({}, state);
            newState.bookingInfo.imageEditorFirmId = action.result;
            newState.bookingInfo.imageEditorId = null;
            return newState;

        case ActionTypes.UPDATE_IMAGE_EDITOR_SUCCESS:
            newState = assign({}, state);
            newState.bookingInfo.imageEditorId = action.result;
            return newState;
        case ActionTypes.UPDATE_EXTRA_DELIVERY_RECIPIENTS_SUCCESS:
            newState = assign({}, state);
            newState.extraDeliveryRecipients = action.result;
            return newState;
        case ActionTypes.SET_ORDER_TO_UPLOADER_SUCCESS:
            newState = assign({}, state);
            newState.bookingInfo.photoStatus = action.result;
            return newState;
        case ActionTypes.UPDATE_ORDER_STATE_SUCCESS:
            newState = assign({}, state);
            newState.orderState = action.result;
            return newState;
        case ActionTypes.LOCK_PHOTOGRAPHER_SUCCESS:
            newState = assign({}, state);
            newState.bookingInfo.customerWantsClient = true;
            return newState;
        case ActionTypes.UNLOCK_PHOTOGRAPHER_SUCCESS:
            newState = assign({}, state);
            newState.bookingInfo.customerWantsClient = false;
            return newState;
        default:
            return state;
    }
}

export let selectBasicOrderInfo = (state: any) => state.basicOrderInfo;
export let selectBasicOrderInfoSms = (state: any) => selectBasicOrderInfo(state).sms;
export let selectCustomer = (state: any) => selectBasicOrderInfo(state).customer;
export let selectIsCustomerAllowedToBook = (state: any) => selectCustomer(state).isCustomerAllowedToBook;
